import React, { useEffect, useMemo, useRef, useState } from 'react'

import { CloseIcon, LockLockedIcon } from '@toasttab/buffet-pui-icons'
import classNames from 'classnames'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import {
  RewardIcon,
  TagIcon
} from 'src/public/components/default_template/offers/OffersIcons'
import Image from 'src/shared/components/common/Image'
import { t } from 'src/translations'

import ContextualLoadingSpinner from 'shared/components/common/loading_spinner/LoadingSpinner'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { getRestrictedDiningOption } from 'public/components/default_template/online_ordering/cart/offers/OfferUtils'
import { useCart } from 'public/components/online_ordering/CartContext'

export enum DiscountPillState {
  ACTIVE,
  LOCKED,
  INACTIVE
}
export enum DiscountPillType {
  OFFER,
  REWARD,
  BUTTON
}
type DiscountPillProps = {
  text: string
  onClick?: () => void | Promise<unknown>
  pillKey?: string
  state?: DiscountPillState
  type?: DiscountPillType
  loadingOverride?: boolean
  testId?: string
  triggerDiningOptions?: string[] | null
}

const DiscountPill = ({
  text,
  onClick,
  pillKey,
  state = DiscountPillState.INACTIVE,
  type = DiscountPillType.OFFER,
  loadingOverride,
  testId,
  triggerDiningOptions
}: DiscountPillProps) => {
  const pillTextRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false)
  const { diningOptions } = useRestaurant()
  const { cart } = useCart()
  const restrictedDiningOption = getRestrictedDiningOption(
    triggerDiningOptions,
    diningOptions
  )
  const deliveryOnly = restrictedDiningOption === DiningOptionBehavior.Delivery
  const takeoutOnly = restrictedDiningOption === DiningOptionBehavior.TakeOut
  const pillState = useMemo(
    () =>
      // If the discount is restricted to a dining option that doesn't match the dining option for the cart,
      // treat it as a locked discount.
      restrictedDiningOption &&
      restrictedDiningOption !== cart?.diningOptionBehavior
        ? DiscountPillState.LOCKED
        : state,
    [restrictedDiningOption, cart?.diningOptionBehavior, state]
  )

  const icon = useMemo(() => {
    if (type === DiscountPillType.BUTTON) {
      return null
    }
    if (pillState === DiscountPillState.ACTIVE) {
      return (
        <div className='checkContainer'>
          <Image
            alt={t('discount-pill.discount-is-active')}
            src='icons/check.svg'
            className='checkIcon'
            data-testid='checkmark'
          />
        </div>
      )
    }
    if (takeoutOnly) {
      return (
        <div className='tagIcon' data-testid='pickup-icon'>
          <Image alt={t('discount-pill.pickup-only')} src='icons/pickup.svg' />
        </div>
      )
    }
    if (deliveryOnly) {
      return (
        <div className='tagIcon' data-testid='delivery-icon'>
          <Image
            alt={t('discount-pill.delivery-only')}
            src='icons/delivery.svg'
          />
        </div>
      )
    }
    if (pillState === DiscountPillState.LOCKED) {
      return (
        <div
          className='tagIcon'
          data-testid='lock-icon'
          aria-label={t('discount-pill.discount-is-locked')}
        >
          <LockLockedIcon />
        </div>
      )
    }
    return (
      <div className='tagIcon' data-testid='tag-icon'>
        {type === DiscountPillType.OFFER ? (
          <TagIcon
            color='#646464'
            altText={t('discount-pill.offer-available')}
          />
        ) : (
          <RewardIcon
            color='#646464'
            altText={t('discount-pill.reward-available')}
          />
        )}
      </div>
    )
  }, [pillState, type, deliveryOnly, takeoutOnly])

  useEffect(() => {
    // changes the font size of all the pills if one of them is truncated
    if (
      pillTextRef.current &&
      (pillTextRef.current.offsetWidth < pillTextRef.current.scrollWidth ||
        pillTextRef.current.offsetHeight < pillTextRef.current.scrollHeight)
    ) {
      document.querySelectorAll('.discountText').forEach((div) => {
        div.classList.add('smallerText')
      })
    }
  })

  const handleClick = async () => {
    // Prevents a second click from starting a concurrent cart mutation.
    if (loading) {
      return
    }
    setLoading(true)
    try {
      await onClick?.()
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      className={classNames('discountPill', {
        active: pillState === DiscountPillState.ACTIVE,
        locked: pillState === DiscountPillState.LOCKED,
        buttonPill: type === DiscountPillType.BUTTON
      })}
      onClick={handleClick}
      key={pillKey}
      type='button'
      data-testid={testId}
      aria-busy={loading}
      // Kept focusable while the mutation runs — `disabled` drops focus to the body.
      aria-disabled={loading || undefined}
      disabled={loadingOverride || pillState === DiscountPillState.LOCKED}
    >
      {loading ? (
        <ContextualLoadingSpinner size='28px' strokeWidth='3px' />
      ) : (
        icon
      )}
      <div className='discountText' ref={pillTextRef}>
        {text}
      </div>
      {pillState === DiscountPillState.ACTIVE && (
        <div className='activeActions'>
          <div className='divider' />
          <CloseIcon size='xs' testId='close-icon' className='closeIcon' />
        </div>
      )}
    </button>
  )
}

export default DiscountPill
